import initSqlJs from "sql.js";
import { preprocessSqlQuery } from "./submitHandlers";
import { loadDbFromIndexedDB } from "../preOffline/indexedDbHelper";

const DB_STORAGE_KEY = "sqljs_db";

// Shared helper to initialize DB
const getDatabase = async () => {
  const SQL = await initSqlJs({
    locateFile: () => "sql-wasm.wasm",
  });

  const binaryArray = await loadDbFromIndexedDB();

  if (!binaryArray) return null;

  return new SQL.Database(binaryArray);
};

// Helper to convert rows to array of objects
const parseQueryResult = (result) => {
  if (!result.length) return [];

  const { columns, values } = result[0];
  return values.map((row) =>
    Object.fromEntries(
      columns.map((col, i) => [
        col,
        col === "id" && !isNaN(row[i])
          ? parseInt(row[i], 10)
          : row[i] === ""
          ? null
          : row[i],
      ])
    )
  );
};

// Run a SQL query (supports views)
export const getDataFromSqlJs = async (query) => {
  try {
    const db = await getDatabase();
    if (!db) {
      console.error(
        "Database not found. Please call fetchAndStoreAllTablesData first."
      );
      return [];
    }

    const viewRegex = /\bfrom\s+(\w+View)\b/i;
    const match = query.match(viewRegex);



    if (match) {
      const viewName = match[1];
      const viewResult = db.exec(
        `SELECT sqlQuery FROM views WHERE viewName = '${viewName}'`
      );

      if (!viewResult.length || !viewResult[0].values[0][0]) {
        console.warn(`View "${viewName}" not found or empty.`);
        db.close();
        return [];
      }

      const viewSQL = viewResult[0].values[0][0];
      query = query.replace(viewRegex, `FROM (${viewSQL})`);
    }

   // ✅ Only preprocess if these functions are present
    const functionsRegex = /\b(greatest|least|STDDEV|VARIANCE)\s*\(/i;
    let finalQuery = query;
    if (functionsRegex.test(query)) {
      finalQuery = await preprocessSqlQuery(query);
    }

  

    const result = db.exec(finalQuery);
    const data = await parseQueryResult(result);
    db.close();
    return data;
  } catch (error) {
    console.error("Error executing select query:", query, error);
    return [];
  }
};

// Get all data from all tables
export const getAllTablesDataFromSqlJs = async () => {
  try {
    const db = await getDatabase();
    if (!db) {
      console.error(
        "Database not found. Please call fetchAndStoreAllTablesData first."
      );
      return {};
    }

    const tablesResult = db.exec(
      "SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%';"
    );
    if (!tablesResult.length) {
      db.close();
      return {};
    }

    const tableNames = tablesResult[0].values.map((row) => row[0]);
    const allTablesData = {};

    tableNames.forEach((table) => {
      const result = db.exec(`SELECT * FROM ${table}`);
      allTablesData[table] = parseQueryResult(result);
    });

    db.close();
    return allTablesData;
  } catch (error) {
    console.error("Error loading all tables data:", error);
    return {};
  }
};

// Get stored Axios requests from `allAxiosRequest` table
export const getStoredAxiosRequests = async () => {
  try {
    const db = await getDatabase();
    if (!db) return [];

    const result = db.exec("SELECT * FROM allAxiosRequest");
    const data = parseQueryResult(result);
    db.close();
    return data;
  } catch (error) {
    console.error("Error reading stored Axios requests:", error);
    return [];
  }
};

// Get data by parsing URL (e.g. "/tableName/5")
export const getDataFromUrlSqlJs = async (url) => {
  try {
    const db = await getDatabase();
    if (!db) {
      return { found: false, message: "SQL.js DB not found in localStorage." };
    }

    const parts = url.split("/").filter(Boolean);
    const lastPart = parts[parts.length - 1];
    const isId = /^\d+$/.test(lastPart);
    const id = isId ? lastPart : null;
    const tableName = isId ? parts[parts.length - 2] : lastPart;

    const query = id
      ? `SELECT * FROM "${tableName}" WHERE id = ?`
      : `SELECT * FROM "${tableName}"`;

    const result = id ? db.exec(query, [String(id)]) : db.exec(query);
    const data = parseQueryResult(result);

    db.close();
    return data.length
      ? { found: true, data: id ? data[0] : data }
      : {
          found: false,
          message: `No data found in table "${tableName}"${
            id ? ` with id = ${id}` : ""
          }.`,
        };
  } catch (error) {
    return { found: false, message: "SQL.js error: " + error.message };
  }
};
